import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Seo, { orgSchema, breadcrumbSchema } from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { FadeUp, Eyebrow } from "@/components/Reveal";
import { SITE } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
    const navigate = useNavigate();
    const [sending, setSending] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", consent: false, website: "" });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !/.+@.+\..+/.test(form.email) || !form.message.trim() || !form.consent) {
            toast.error("Please complete name, email, message and consent.");
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/contact`, form);
            navigate("/thank-you");
        } catch (err) {
            toast.error(`Could not send right now. Please email us directly at ${SITE.email}.`);
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <Seo
                title="Contact — Dagaz Development | US & Canada"
                description="Reach Dagaz Development at damonson@dagazdesigns.com. Oregon-based, delivering custom homes, reconstructions and commercial projects across the US and Canada."
                path="/contact"
                jsonLd={[orgSchema, breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }])]}
            />
            <PageHeader
                eyebrow="Contact"
                titleLines={["Write to us.", "We write back."]}
                answer="One inbox, read personally: damonson@dagazdesigns.com. Whether it is a piece of land, a tired building or a half-formed idea, you will hear back within two business days."
                crumbs={[{ name: "Contact" }]}
            />
            <section className="px-6 md:px-12 lg:px-16 pb-28 md:pb-40 bg-chalk grid lg:grid-cols-12 gap-16" data-testid="contact-section">
                <div className="lg:col-span-5 space-y-10">
                    <FadeUp>
                        <Eyebrow className="mb-6">Direct</Eyebrow>
                        <a href={`mailto:${SITE.email}`} data-testid="contact-email-link" className="font-serif font-light text-2xl md:text-4xl text-graphite link-rule break-all">
                            {SITE.email}
                        </a>
                    </FadeUp>
                    {[
                        ["Response", "Within two business days, personally."],
                        ["Office", "Oregon — Pacific Northwest"],
                        ["Coverage", "All US states & Canadian provinces"],
                    ].map(([label, value], i) => (
                        <FadeUp key={label} delay={0.1 * (i + 1)} className="border-t border-graphite/15 pt-5">
                            <p className="font-sans text-[10px] font-medium tracking-[0.28em] uppercase text-slate">{label}</p>
                            <p className="mt-2 font-serif text-xl text-graphite">{value}</p>
                        </FadeUp>
                    ))}
                </div>
                <div className="lg:col-span-6 lg:col-start-7">
                    <FadeUp delay={0.15}>
                        <form onSubmit={submit} className="space-y-7" data-testid="contact-form">
                            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                                <div>
                                    <label htmlFor="c-name" className="block font-sans text-[10px] font-medium tracking-[0.24em] uppercase text-slate mb-2">Full name *</label>
                                    <input id="c-name" data-testid="contact-name" type="text" value={form.name} onChange={(e) => set("name", e.target.value)} className="input-line" placeholder="Your name" />
                                </div>
                                <div>
                                    <label htmlFor="c-email" className="block font-sans text-[10px] font-medium tracking-[0.24em] uppercase text-slate mb-2">Email *</label>
                                    <input id="c-email" data-testid="contact-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input-line" placeholder="you@example.com" />
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="c-phone" className="block font-sans text-[10px] font-medium tracking-[0.24em] uppercase text-slate mb-2">Phone</label>
                                    <input id="c-phone" data-testid="contact-phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="input-line" placeholder="Optional" />
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="c-message" className="block font-sans text-[10px] font-medium tracking-[0.24em] uppercase text-slate mb-2">Message *</label>
                                    <textarea id="c-message" data-testid="contact-message" rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} className="input-line resize-none" placeholder="Tell us about the site, the building, or the idea." />
                                </div>
                            </div>
                            <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" aria-hidden="true" />
                            <label className="flex items-start gap-3 cursor-pointer" data-testid="contact-consent-label">
                                <input type="checkbox" data-testid="contact-consent" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1 w-4 h-4 accent-[#D4AF37]" />
                                <span className="font-sans text-xs leading-relaxed text-slate">
                                    I agree that Dagaz Development may contact me about this message by email or phone. *
                                </span>
                            </label>
                            <button
                                type="submit"
                                data-testid="contact-submit"
                                disabled={sending}
                                className="group inline-flex items-center gap-3 px-8 py-4 bg-forest text-chalk font-sans text-xs font-semibold tracking-[0.22em] uppercase transition-colors duration-500 hover:bg-graphite disabled:opacity-50"
                            >
                                {sending ? "Sending…" : "Send Message"}
                                <span className="inline-block transition-transform duration-500 group-hover:translate-x-1.5" aria-hidden="true">→</span>
                            </button>
                        </form>
                    </FadeUp>
                </div>
            </section>
        </>
    );
}
